/**
 * PostHog Analytics Utility for SmartPlAIte
 * 
 * Centralizes all analytics tracking with type-safe event names.
 * Initialize once in App.js, then use track() throughout the app.
 */

import posthog from 'posthog-js';

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let isInitialized = false;

// ═══════════════════════════════════════════════════════════════════════════════
// EVENT NAMES (Type-safe event catalog)
// ═══════════════════════════════════════════════════════════════════════════════
export const EVENTS = {
  // Auth Events
  USER_SIGNED_UP: 'user_signed_up',
  USER_LOGGED_IN: 'user_logged_in',
  USER_LOGGED_OUT: 'user_logged_out',
  SESSION_EXPIRED: 'session_expired',
  
  // Onboarding Events
  PROFILE_CREATED: 'profile_created',
  SMART_GOALS_COMPLETED: 'smart_goals_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  
  // Meal Logging Events
  MEAL_LOGGED: 'meal_logged',
  MEAL_DELETED: 'meal_deleted',
  MEAL_FAVORITED: 'meal_favorited',
  
  // Smart Picks Events
  SMART_PICKS_OPENED: 'smart_picks_opened',
  RESTAURANT_SEARCHED: 'restaurant_searched',
  RESTAURANT_ITEM_LOGGED: 'restaurant_item_logged',
  
  // Smart Swaps Events
  SMART_SWAPS_OPENED: 'smart_swaps_opened',
  SMART_SWAPS_SEARCHED: 'smart_swaps_searched',
  SMART_SWAP_RECIPE_VIEWED: 'smart_swap_recipe_viewed',
  SMART_SWAP_LOGGED: 'smart_swap_logged',
  SMART_SWAP_SAVED: 'smart_swap_saved',
  
  // Smart Search (Home Recipes) Events - Legacy
  RECIPE_SEARCHED: 'recipe_searched',
  RECIPE_VIEWED: 'recipe_viewed',
  RECIPE_LOGGED: 'recipe_logged',
  RECIPE_SAVED: 'recipe_saved',
  
  // Smart Hub Events
  SMART_HUB_OPENED: 'smart_hub_opened',
  SMART_HUB_VIEWED: 'smart_hub_viewed',
  FAVORITE_LOGGED_FROM_HUB: 'favorite_logged_from_hub',
  FAVORITE_REMOVED: 'favorite_removed',
  
  // Feature Usage Events
  CAMERA_OPENED: 'camera_opened',
  PHOTO_UPLOADED: 'photo_uploaded',
  MANUAL_ENTRY_OPENED: 'manual_entry_opened',
  BARCODE_SCANNED: 'barcode_scanned',
  MIQ_VIEW_OPENED: 'miq_view_opened',
  DAILY_CHECKIN_OPENED: 'daily_checkin_opened',
  COOKBOOK_OPENED: 'cookbook_opened',
  MEAL_PLANNER_OPENED: 'meal_planner_opened',
  
  // Subscription/Upgrade Events
  UPGRADE_MODAL_OPENED: 'upgrade_modal_opened',
  UPGRADE_CTA_CLICKED: 'upgrade_cta_clicked',
  LOCKED_CTA_CLICKED: 'locked_cta_clicked',
  CHECKOUT_STARTED: 'checkout_started',
  SUBSCRIPTION_ACTIVATED: 'subscription_activated',
  
  // Engagement Events
  TAB_SWITCHED: 'tab_switched',
  PAGE_VIEWED: 'page_viewed',
  FEATURE_DISCOVERED: 'feature_discovered'
};

// ═══════════════════════════════════════════════════════════════════════════════
// INITIALIZATION
// ═══════════════════════════════════════════════════════════════════════════════
export const initAnalytics = () => {
  if (isInitialized || !POSTHOG_KEY) {
    if (!POSTHOG_KEY) {
      console.warn('[Analytics] PostHog key not configured');
    }
    return;
  }
  
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Capture pageviews automatically
      capture_pageview: true,
      // Capture page leaves
      capture_pageleave: true,
      // Respect Do Not Track
      respect_dnt: true,
      // Disable in development if needed
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') {
          // Optionally disable in dev: posthog.opt_out_capturing()
          console.log('[Analytics] PostHog initialized (dev mode)');
        }
      },
      // Session recording (optional - enable if needed)
      disable_session_recording: true,
      // Autocapture clicks on buttons/links
      autocapture: {
        dom_event_allowlist: ['click'],
        element_allowlist: ['button', 'a', 'input[type="submit"]']
      }
    });
    
    isInitialized = true;
    console.log('[Analytics] PostHog initialized successfully');
  } catch (err) {
    console.error('[Analytics] PostHog initialization failed:', err);
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// USER IDENTIFICATION
// ═══════════════════════════════════════════════════════════════════════════════
export const identifyUser = (userId, properties = {}) => {
  if (!isInitialized || !userId) return;
  
  try {
    posthog.identify(userId, {
      ...properties,
      identified_at: new Date().toISOString()
    });
    console.log('[Analytics] User identified:', userId);
  } catch (err) {
    console.error('[Analytics] identify error:', err);
  }
};

export const setUserProperties = (properties) => {
  if (!isInitialized) return;
  
  try {
    posthog.people.set(properties);
  } catch (err) {
    console.error('[Analytics] setUserProperties error:', err);
  }
};

export const resetUser = () => {
  if (!isInitialized) return;
  
  try {
    posthog.reset();
    console.log('[Analytics] User reset');
  } catch (err) {
    console.error('[Analytics] reset error:', err);
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// EVENT TRACKING
// ═══════════════════════════════════════════════════════════════════════════════
export const track = (eventName, properties = {}) => {
  if (!isInitialized) {
    // Queue events before init? For now just skip
    return;
  }
  
  try {
    posthog.capture(eventName, {
      ...properties,
      timestamp: new Date().toISOString()
    });
  } catch (err) {
    console.error('[Analytics] track error:', err);
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONVENIENCE METHODS
// ═══════════════════════════════════════════════════════════════════════════════

// Track meal logged with source context
export const trackMealLogged = (source, mealData) => {
  track(EVENTS.MEAL_LOGGED, {
    source, // 'camera', 'manual', 'smart_picks', 'smart_search', 'smart_hub', 'barcode'
    calories: mealData.calories,
    has_protein: mealData.protein > 0,
    has_macros: mealData.protein > 0 || mealData.carbs > 0 || mealData.fat > 0,
    meal_type: mealData.meal_type
  });
};

// Track recipe search
export const trackRecipeSearch = (query, resultsCount) => {
  track(EVENTS.RECIPE_SEARCHED, {
    query_length: query.length,
    results_count: resultsCount
  });
};

// Track upgrade funnel
export const trackUpgradeCTA = (location, targetTier) => {
  track(EVENTS.UPGRADE_CTA_CLICKED, {
    location, // 'home', 'smart_picks', 'miq', 'settings'
    target_tier: targetTier // 'smart_plaite', 'genius_plaite'
  });
};

/**
 * Track when a user taps a locked action and is shown Upgrade/Paywall
 * STRICTMODE-safe: Only fires when handler is invoked (not on render)
 * 
 * @param {Object} params
 * @param {string} params.feature - Feature name: "smartmeals", "daily_checkin", "miq_baseline_report", etc.
 * @param {string} params.tier - Current user tier: "free", "smart", "genius"
 * @param {string} params.source_view - View where action was clicked: "miq_tab", "home", "settings"
 * @param {string} params.reason - Why locked: "tier_locked", "trial_expired"
 * @param {number|null} params.day_in_trial - Current day in trial or null
 */
export const trackLockedCtaClicked = ({
  feature,
  tier,
  source_view,
  reason = 'tier_locked',
  day_in_trial = null
}) => {
  track(EVENTS.LOCKED_CTA_CLICKED, {
    feature,
    tier,
    source_view,
    reason,
    day_in_trial
  });
};

// Track feature usage
export const trackFeatureOpened = (featureName) => {
  track(EVENTS.PAGE_VIEWED, {
    feature: featureName
  });
};

// ═══════════════════════════════════════════════════════════════════════════════
// FEATURE FLAGS (Optional)
// ═══════════════════════════════════════════════════════════════════════════════
export const isFeatureEnabled = (flagName) => {
  if (!isInitialized) return false;
  
  try {
    return posthog.isFeatureEnabled(flagName);
  } catch (err) {
    return false;
  }
};

export const getFeatureFlag = (flagName, defaultValue = null) => {
  if (!isInitialized) return defaultValue;
  
  try {
    return posthog.getFeatureFlag(flagName) ?? defaultValue;
  } catch (err) {
    return defaultValue;
  }
};

// Export posthog instance for advanced usage
export { posthog };

// Default export for simple import
export default {
  init: initAnalytics,
  identify: identifyUser,
  setUserProperties,
  reset: resetUser,
  track,
  EVENTS,
  trackMealLogged,
  trackRecipeSearch,
  trackUpgradeCTA,
  trackLockedCtaClicked,
  trackFeatureOpened,
  isFeatureEnabled,
  getFeatureFlag
};
